import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb, { getBreadcrumbForCategory } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, categoryMeta } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const webPageSchema = (slug: string) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: categoryMeta[slug]?.title || '文章分类',
  description: categoryMeta[slug]?.description || '',
  url: `https://mingcidianzi162.cn/category/${slug}`,
  inLanguage: 'zh-CN',
  isPartOf: {
    '@type': 'WebSite',
    name: '91浏览器',
    url: 'https://mingcidianzi162.cn',
  },
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'features');

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const cat = categories.find((c) => c.slug === activeTab);
  const meta = categoryMeta[activeTab];
  const filteredArticles = articles.filter((a) => a.category === activeTab);
  const breadcrumbItems = getBreadcrumbForCategory(activeTab);

  return (
    <>
      <SeoHead
        title={meta?.title || '91浏览器文章分类'}
        description={meta?.description || ''}
        keywords={meta?.keywords || ''}
        canonicalUrl={`/category/${activeTab}`}
        schema={webPageSchema(activeTab)}
        schemaType="WebPage"
      />

      <main className="min-h-screen bg-background-50 pt-24 pb-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {cat?.name || '文章分类'}
            </h1>
            <p className="text-sm text-foreground-600">{cat?.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10 overflow-x-auto pb-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === c.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${c.icon} text-sm`}></i>
                {c.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}